import React, { useEffect, useState } from "react";
import clsx from "clsx";
import moment from "moment";
import Link from "common/Link";
import DatePicker from "react-datepicker";
import { UrlUtils } from "utils/url-utils";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import { ReactComponent as ChevronDownIcon } from "assets/icons/dropdown-arrow.svg";

const inputClassNames =
  "w-full px-3 py-2 leading-normal bg-white border border-obituary-brown-300 focus:border-obituary-brown-300 focus:ring-0 focus:outline-0 placeholder:text-gray-500";

const tomorrow = moment().add(1, "d").format("YYYY-MM-DD");

const SearchStripe = ({
  name,
  setName,
  dateRange,
  setDateRange,
  isBranch,
  path,
  searchObits,
}) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const dateRangeOptions = [
    {
      id: "0",
      value: "Today",
      startDate: moment().format("YYYY-MM-DD"),
      endDate: tomorrow,
      disabled: false,
    },
    {
      id: "1",
      value: "Past 3 days",
      startDate: moment().subtract(3, "d").format("YYYY-MM-DD"),
      endDate: tomorrow,
      disabled: false,
    },
    {
      id: "2",
      value: "Past 7 days",
      startDate: moment().subtract(1, "w").format("YYYY-MM-DD"),
      endDate: tomorrow,
      disabled: false,
    },
    {
      id: "3",
      value: "Past 2 weeks",
      startDate: moment().subtract(2, "w").format("YYYY-MM-DD"),
      endDate: tomorrow,
      disabled: false,
    },
    {
      id: "4",
      value: "Past 30 days",
      startDate: moment().subtract(30, "d").format("YYYY-MM-DD"),
      endDate: tomorrow,
      disabled: false,
    },
    {
      id: "5",
      value: "Past 6 months",
      startDate: moment().subtract(6, "months").format("YYYY-MM-DD"),
      endDate: tomorrow,
      disabled: false,
    },
    {
      id: "6",
      value: "Past year",
      startDate: moment().subtract(1, "y").format("YYYY-MM-DD"),
      endDate: tomorrow,
      disabled: false,
    },
    {
      id: "7",
      value: "Choose custom date range",
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).add(1, "d").format("YYYY-MM-DD"),
      disabled: !name,
    },
    {
      id: "8",
      value: "All time",
      startDate: "1900-01-01",
      endDate: tomorrow,
      disabled: !name,
    },
  ];

  const handleDateRangeChange = (e) => {
    const selected = dateRangeOptions.find((el) => el.value === e.target.value);
    if (e.target.value === "Choose custom date range") {
      setDateRange({ selected });
    }
    setDateRange(selected);
  };

  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    if (dateRange.value === "Choose custom date range") {
      setDateRange({
        ...dateRange,
        startDate: moment(date).format("YYYY-MM-DD"),
      });
    }
  };

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
    if (dateRange.value === "Choose custom date range") {
      setDateRange({
        ...dateRange,
        endDate: moment(date).add(1, "d").format("YYYY-MM-DD"),
      });
    }
  };

  // removes the branch name from the path if exists
  const getCorporatePath = (path: string) => {
    const parts = path.split("/");
    if (isBranch && parts.length === 3) {
      parts.splice(2, 1); // remove the second part, which is the branch name
    }

    return parts.join("/");
  };

  useEffect(() => {
    if (!name && ["7", "8"].includes(dateRange.id)) {
      setDateRange(dateRangeOptions[2]);
    }
  }, [name]);

  return (
    <>
      <div className="w-full px-2 py-3 space-y-4 bg-[#dbd7be]">
        <h4 className="text-sm font-semibold text-center text-obituary-brown-300">
          Search Obituaries
        </h4>
        <div
          className={`${
            dateRange.value === "Choose custom date range"
              ? "grid-cols-5"
              : "grid-cols-3"
          } grid w-full gap-2`}
        >
          <input
            type="text"
            className={inputClassNames}
            placeholder={`${t("obits.searchObitsPlaceholder")}`}
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchObits()}
          />
          <div className="relative">
            <select
              id="date_range"
              name="date-range"
              className={clsx(inputClassNames, "appearance-none pr-9 truncate")}
              value={dateRange.value}
              onChange={handleDateRangeChange}
            >
              {dateRangeOptions.map((option) => (
                <option
                  key={option.id}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.value}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <ChevronDownIcon className="size-4 text-obituary-brown-300" />
            </div>
          </div>
          {dateRange.value === "Choose custom date range" && (
            <>
              <div>
                <DatePicker
                  className={inputClassNames}
                  placeholderText="From"
                  selected={startDate}
                  maxDate={endDate || new Date()}
                  onChange={handleStartDateChange}
                />
              </div>
              <div>
                <DatePicker
                  className={inputClassNames}
                  placeholderText="To"
                  selected={endDate}
                  minDate={startDate}
                  maxDate={new Date()}
                  onChange={handleEndDateChange}
                />
              </div>
            </>
          )}
          <button
            type="button"
            className="w-full px-4 py-2 text-sm font-bold text-white bg-obituary-brown-300"
            onClick={searchObits}
          >
            Find
          </button>
        </div>
        <div className="text-center">
          {isBranch && (
            <Link
              to={`${getCorporatePath(
                UrlUtils.getShortUrl(path)
              )}/obituaries.html`}
              className="text-sm text-obituary-brown-300"
            >
              {t("search.corporateObitsSearchLabel")}
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchStripe;
